import { useEffect, useRef, useState } from 'react';
import { Package } from 'lucide-react';

const ITEM_HEIGHT = 28;
const VISIBLE_ITEMS = 3;
const SCROLL_INTERVAL_MS = 2000;
const MAX_TITLE_LENGTH = 50;

function truncateTitle(title: string): string {
  if (title.length <= MAX_TITLE_LENGTH) return title;
  return title.slice(0, MAX_TITLE_LENGTH - 1) + '\u2026';
}

interface RollingTitlesProps {
  titles: string[];
  icon?: React.ReactNode;
}

export function RollingTitles({ titles, icon }: RollingTitlesProps) {
  const queueRef = useRef<string[]>([]);
  const [visibleItems, setVisibleItems] = useState<string[]>([]);
  const [offset, setOffset] = useState(0);
  const indexRef = useRef(0);

  useEffect(() => {
    if (!titles || titles.length === 0) return;
    const q = queueRef.current;
    for (const t of titles) {
      if (!q.includes(t)) q.push(t);
    }
    if (visibleItems.length === 0 && q.length > 0) {
      const initial = q.slice(0, VISIBLE_ITEMS + 1);
      setVisibleItems(initial);
      indexRef.current = initial.length;
    }
  }, [titles]);

  useEffect(() => {
    if (visibleItems.length === 0) return;
    const timer = setInterval(() => {
      setOffset(-ITEM_HEIGHT);
      setTimeout(() => {
        setOffset(0);
        setVisibleItems(prev => {
          const q = queueRef.current;
          if (q.length === 0) return prev;
          const next = [...prev.slice(1)];
          const nextTitle = q[indexRef.current % q.length];
          indexRef.current++;
          next.push(nextTitle);
          return next;
        });
      }, 600);
    }, SCROLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [visibleItems.length > 0]);

  if (visibleItems.length === 0) return null;

  const iconElement = icon ?? <Package className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />;

  return (
    <div
      className="relative overflow-hidden"
      style={{
        height: ITEM_HEIGHT * VISIBLE_ITEMS,
        maskImage: 'linear-gradient(to bottom, transparent 0%, black 30%, black 70%, transparent 100%)',
        WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 30%, black 70%, transparent 100%)',
      }}
    >
      <div
        style={{
          transform: `translateY(${offset}px)`,
          transition: offset !== 0 ? 'transform 600ms cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
        }}
      >
        {visibleItems.map((title, i) => (
          <div
            key={`${i}-${title}`}
            className="flex items-center gap-2 text-xs text-muted-foreground px-1"
            style={{ height: ITEM_HEIGHT }}
          >
            {iconElement}
            <span className="truncate max-w-[340px]">{truncateTitle(title)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
